import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DeleteConfirmModal from './DeleteConfirmModal';
import SuccessModal from './SuccessModal';

const API_BASE_URL = 'https://www.testometer.in:8084/api';

const ResumeList = ({ onCreateResume, onEditResume, hideHeader = false }) => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showAddSkillModal, setShowAddSkillModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const resumesPerPage = 5;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resumeToDelete, setResumeToDelete] = useState(null);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchResumes();
  }, []);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchResumes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/resumes`);
      setResumes(response.data);
    } catch (error) {
      console.error('Error fetching resumes:', error);
      setError('Failed to load resumes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteResume = async (resumeId) => {
    try {
      await axios.delete(`${API_BASE_URL}/resumes/${resumeId}`);
      fetchResumes(); // Refresh the list
    } catch (error) {
      console.error('Error deleting resume:', error);
    }
  };

  const handleCopyResume = async (resumeId) => {
    try {
      await axios.post(`${API_BASE_URL}/resumes/${resumeId}/copy`);
      fetchResumes(); // Refresh the list
      setShowSuccessModal(true);
      setSuccessMessage('Resume copied successfully!');
    } catch (error) {
      console.error('Error copying resume:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredAndSortedResumes = resumes
    .filter(resume =>
      resume.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resume.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else {
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedResumes.length / resumesPerPage);
  const indexOfLastResume = currentPage * resumesPerPage;
  const indexOfFirstResume = indexOfLastResume - resumesPerPage;
  const currentResumes = filteredAndSortedResumes.slice(indexOfFirstResume, indexOfLastResume);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {indexOfFirstResume + 1} to {Math.min(indexOfLastResume, filteredAndSortedResumes.length)} of {filteredAndSortedResumes.length} resumes
        </div>
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn btn-secondary pagination-btn"
          >
            ← Previous
          </button>
          
          {startPage > 1 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className="btn btn-secondary pagination-btn"
              >
                1
              </button>
              {startPage > 2 && <span className="pagination-ellipsis">...</span>}
            </>
          )}
          
          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`btn pagination-btn ${currentPage === number ? 'btn-primary' : 'btn-secondary'}`}
            >
              {number}
            </button>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
              <button
                onClick={() => handlePageChange(totalPages)}
                className="btn btn-secondary pagination-btn"
              >
                {totalPages}
              </button>
            </>
          )}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn btn-secondary pagination-btn"
          >
            Next →
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="resume-list">
        <div className="container">
          <div className="card">
            <div className="loading-overlay">
              <div className="loading-modal">
                <div className="spinner"></div>
                <span>Loading resumes...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="resume-list">
      <div className="container">
        <div className="card">
          {/* Header */}
          {!hideHeader && (
            <div className="resume-list-header">
              <div>
                <h1>Resume Management</h1>
                <p>Manage and create your professional resumes</p>
              </div>
              <button
                onClick={onCreateResume}
                className="btn btn-primary"
              >
                + Create New Resume
              </button>
            </div>
          )}

          {/* Search and Stats */}
          <div className="resume-list-controls">
            <div className="search-section">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field"
                placeholder="Search resumes by name or email..."
              />
            </div>
            <div className="stats-section">
              <span className="stats-text">
                Total Resumes: {resumes.length}
              </span>
              {searchTerm && (
                <span className="stats-text">
                  Filtered: {filteredAndSortedResumes.length}
                </span>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Resumes Table */}
          {filteredAndSortedResumes.length === 0 ? (
            <div className="no-resumes">
              {searchTerm ? (
                <div>
                  <h3>No resumes found</h3>
                  <p>No resumes match your search criteria.</p>
                </div>
              ) : (
                <div>
                  <h3>No resumes yet</h3>
                  <p>Create your first resume to get started!</p>
                  <button
                    onClick={onCreateResume}
                    className="btn btn-primary"
                  >
                    Create Your First Resume
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="projects-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')} className="sortable">
                        Name {getSortIcon('name')}
                      </th>
                      <th onClick={() => handleSort('email')} className="sortable">
                        Email {getSortIcon('email')}
                      </th>
                      <th>Contact</th>
                      <th onClick={() => handleSort('createdAt')} className="sortable">
                        Created {getSortIcon('createdAt')}
                      </th>
                      <th onClick={() => handleSort('updatedAt')} className="sortable">
                        Updated {getSortIcon('updatedAt')}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentResumes.map(resume => (
                      <tr key={resume.id}>
                        <td className="project-name">
                          <strong>{resume.name}</strong>
                          {/* <div className="social-links">
                            {resume.linkedin && (
                              <a 
                                href={resume.linkedin} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="social-link"
                                title="LinkedIn"
                              >
                                🔗
                              </a>
                            )}
                            {resume.github && (
                              <a 
                                href={resume.github} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="social-link"
                                title="GitHub"
                              >
                                📱
                              </a>
                            )}
                          </div> */}
                        </td>
                        <td>{resume.email}</td>
                        <td>{resume.mobile}</td>
                        <td>{formatDate(resume.createdAt)}</td>
                        <td>
                          {resume.updatedAt && resume.updatedAt !== resume.createdAt 
                            ? formatDateTime(resume.updatedAt)
                            : formatDate(resume.createdAt)
                          }
                        </td>
                        <td className="action-cell">
                          <div className="action-buttons" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditResume(resume);
                              }}
                              className="btn btn-small btn-primary"
                              title="Edit Resume"
                              style={{ 
                                padding: '4px', 
                                fontSize: '14px',
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px'
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyResume(resume.id);
                              }}
                              className="btn btn-small btn-secondary"
                              title="Copy Resume"
                              style={{ 
                                padding: '4px', 
                                fontSize: '14px',
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px'
                              }}
                            >
                              📋
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setResumeToDelete(resume.id);
                                setShowDeleteModal(true);
                              }}
                              className="btn btn-small btn-danger"
                              title="Delete Resume"
                              style={{ 
                                padding: '4px', 
                                fontSize: '14px',
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px'
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {renderPagination()}
            </>
          )}
        </div>
      </div>
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => { handleDeleteResume(resumeToDelete); setResumeToDelete(null); }}
        message="Are you sure you want to delete this resume? This action cannot be undone."
        title="Delete Resume"
      />
      <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} message={successMessage} />
    </div>
  );
};

export default ResumeList; 